import * as React from 'react';
import CollapseElem from 'Components/Collapse';
import HistoryRow from 'Components/HistoryRow';
import HistoryRowControls from 'Components/HistoryRowControls';
import HistoryRowVerticalItem from 'Components/HistoryRowVerticalItem';
import HistorySection from 'Components/HistorySection';
import DocumentCreationButton from 'Components/DocumentCreationButton';
import { EditingLockedContentContext } from 'Containers/EditingLockedContentContext';
import { formatPartialDate } from 'Utility/date';
import { fm } from 'Components/FormatMessage';
import { sortDocumentsDescending } from 'Utility/documentHandling';

const StyledRow = ({ title, value, condition, id }: IStyledRow): React.JSX.Element => (
  <React.Fragment>
    <span id={id} />
    {condition === true ? <HistoryRowVerticalItem header={title} value={value ?? '-'} /> : undefined}
  </React.Fragment>
);

const ScintigraphyDataSection = ({ document }: IScintigraphyDataSection): React.JSX.Element => {
  const showProcedureCode = document.procedureCode && document.procedureCode.length > 0;
  const showStatement = document.statement && document.statement.length > 0;

  return (
    <React.Fragment>
      {showProcedureCode && (
        <StyledRow
          id={'procedureCode'}
          title={fm('imaging.procedureCode')}
          value={document.procedureCode}
          condition={true}
        />
      )}
      {showStatement && (
        <StyledRow id={'statement'} title={fm('imaging.statementText')} value={document.statement} condition={true} />
      )}
      {document.imagingType || document.result ? (
        <React.Fragment>
          <StyledRow
            id={'imagingTypeHistory'}
            title={fm('imaging.imagingType')}
            value={
              document.imagingType && document.imagingType !== 'other'
                ? fm(`imaging.opts.imagingType.${document.imagingType}`)
                : document.imagingType && document.otherType && document.otherType.length > 0
                  ? document.otherType
                  : document.imagingType && fm(`imaging.opts.imagingType.${document.imagingType}`)
            }
            condition={true}
          />
          {document.imagingType && document.imagingType === 'pet' && (
            <StyledRow
              id={'tracerHistory'}
              title={fm('imaging.tracer')}
              value={
                document.tracer && document.tracer === 'FDG'
                  ? fm(`imaging.opts.tracer.${document.tracer}`)
                  : document.tracer === 'other' && document.otherTracer && document.otherTracer.length > 0
                    ? document.otherTracer
                    : document.tracer && fm(`imaging.opts.tracer.${document.tracer}`)
              }
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              condition={document.imagingType ? true : false}
            />
          )}
          <StyledRow
            id={'amyloidTracerHistory'}
            title={fm('imaging.amyloidTracer')}
            value={document.amyloidTracer && fm(`imaging.opts.amyloidTracer.${document.amyloidTracer}`)}
            condition={document.tracer === 'amyloid'}
          />
          <StyledRow
            id={'amyloidTracerOtherHistory'}
            title={fm('imaging.whatTracer')}
            value={document.otherTracer}
            condition={!!(document.tracer === 'amyloid' && document.otherTracer)}
          />
          <StyledRow
            id={'resultHistory'}
            title={fm('imaging.result')}
            value={document.result && fm(`imaging.opts.result.${document.result}`)}
            condition={true}
          />
          <StyledRow
            id={'symmetryHistory'}
            title={fm('imaging.symmetryOfFinding')}
            value={document.symmetryOfFinding && fm(`imaging.opts.symmetricAsymmetric.${document.symmetryOfFinding}`)}
            condition={!!(document.result && document.result !== 'normal' && document.tracer !== 'amyloid')}
          />
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

const Scintigraphy = ({ documents, startEdit }: IOwnProps): React.JSX.Element => {
  const { openEditing } = React.useContext(EditingLockedContentContext);

  return (
    <HistorySection
      headerText={fm('imaging.scintigraphy')}
      newButton={
        <DocumentCreationButton
          name="scintigraphy"
          text={'imaging.newScintigraphy'}
          onClick={startEdit({}, 'scintigraphy')}
          width={15}
          height={3}
          fontSize={14}
          alternate={true}
        />
      }
      hasHistoryRows={true}
    >
      {documents.toSorted(sortDocumentsDescending).map((d, index) => (
        <React.Fragment key={d._id}>
          <HistoryRow
            headerText={formatPartialDate(d.date)}
            rowButton={
              <HistoryRowControls
                document={d}
                startEdit={
                  d._lockedFor && d._source !== null ? doc => () => openEditing(doc, 'imaging.scoliosis') : startEdit
                }
              />
            }
            controlsMargin={false}
          >
            <>
              {index === 0 ? (
                <ScintigraphyDataSection document={d} />
              ) : (
                d.result && (
                  <CollapseElem
                    localeIDs={{ showMessage: 'imaging.show', hideMessage: 'imaging.hide' }}
                    amount={documents.length}
                  >
                    <div style={{ marginBottom: '1rem' }}>
                      <ScintigraphyDataSection document={d} />
                    </div>
                  </CollapseElem>
                )
              )}
            </>
          </HistoryRow>
        </React.Fragment>
      ))}
    </HistorySection>
  );
};

interface IScintigraphyDataSection {
  document: IDocumentWithTypeV2<'scintigraphy'>;
}

interface IStyledRow {
  title: string | React.JSX.Element;
  value?: any;
  condition?: boolean;
  id?: string;
}

interface IOwnProps {
  documents: IDocumentWithTypeV2<'scintigraphy'>[];
  startEdit: StartEdit;
}

export default Scintigraphy;
